import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy01Icon, Tick02Icon } from "hugeicons-react";
import { CloseButton } from "../Layout/Buttons/CloseButton";
import { useToast } from "../../contexts/ToastContext";
import { useAuth } from "../../contexts/AuthContext";
import { commitMemoryImport, previewMemoryImport } from "../../services/api";
import {
  MEMORY_CATEGORY_LABELS,
  MEMORY_EXPORT_PROMPT,
  MEMORY_IMPORT_MAX_CHARS,
} from "../../Data/MemoryImportData";
import { MemoryImportCandidate, MemoryImportPreview } from "../../types/memory";
import "../../Styles/Settings/MemoryImport.css";

type Step = "paste" | "review";

interface ReviewRow extends MemoryImportCandidate {
  key: number;
  selected: boolean;
}

interface MemoryImportModalProps {
  onClose: () => void;
}

const errorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.error || fallback;

const formatSavedAt = (ms: number): string =>
  `Saved ${new Date(ms).toLocaleDateString(undefined, { month: "short", year: "numeric", timeZone: "UTC" })}`;

/**
 * The paste and review state survive closing the modal, so an accidental
 * close doesn't lose an import. Cleared when the import is saved or the user
 * clears it. Keyed per user so a shared browser doesn't mix accounts.
 */
interface ImportDraft {
  step: Step;
  text: string;
  rows: ReviewRow[];
}

const draftKey = (userId: string | undefined) => `memory-import-draft:${userId ?? "anonymous"}`;

const readDraft = (key: string): ImportDraft | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as ImportDraft) : null;
  } catch {
    return null;
  }
};

const writeDraft = (key: string, draft: ImportDraft) => {
  try {
    if (!draft.text && draft.rows.length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // Storage full or blocked; the import still works, it just won't survive a close.
  }
};

const removeDraft = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {}
};

export const MemoryImportModal = ({ onClose }: MemoryImportModalProps) => {
  const toast = useToast();
  const { user } = useAuth();
  const storageKey = draftKey(user?._id);
  const [draft] = useState(() => readDraft(storageKey));
  const [step, setStep] = useState<Step>(draft?.step ?? "paste");
  const [text, setText] = useState(draft?.text ?? "");
  const [copied, setCopied] = useState(false);
  const [rows, setRows] = useState<ReviewRow[]>(draft?.rows ?? []);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedRows = useMemo(
    () => rows.filter((r) => r.selected && r.content.trim()),
    [rows]
  );

  useEffect(() => {
    writeDraft(storageKey, { step, text, rows });
  }, [storageKey, step, text, rows]);

  const clearDraft = () => {
    setText("");
    setRows([]);
    setStep("paste");
    setError(null);
  };

  const handleClose = useCallback(() => {
    if (!isWorking) onClose();
  }, [isWorking, onClose]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleClose]);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(MEMORY_EXPORT_PROMPT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast("Couldn't copy the prompt", "error");
    }
  };

  const runPreview = async () => {
    setIsWorking(true);
    setError(null);
    try {
      const result: MemoryImportPreview = await previewMemoryImport(text.trim());
      setRows(
        result.candidates.map((c, i) => ({ ...c, key: i, selected: true }))
      );
      setStep("review");
    } catch (err) {
      setError(errorMessage(err, "Couldn't read those memories. Please try again."));
    } finally {
      setIsWorking(false);
    }
  };

  const runCommit = async () => {
    setIsWorking(true);
    setError(null);
    try {
      const result = await commitMemoryImport(
        selectedRows.map(({ content, category, priority, expiresAt, savedAt }) => ({
          content: content.trim(),
          category,
          priority,
          expiresAt,
          savedAt,
        }))
      );
      removeDraft(storageKey);
      toast(
        result.created === 1 ? "Imported 1 memory" : `Imported ${result.created} memories`,
        "success"
      );
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Couldn't import those memories. Please try again."));
    } finally {
      setIsWorking(false);
    }
  };

  const updateRow = (key: number, patch: Partial<ReviewRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const setAllSelected = (selected: boolean) =>
    setRows((prev) => prev.map((r) => ({ ...r, selected })));

  return (
    <div className="memory-import-overlay" onClick={handleClose}>
      <div
        className="memory-import-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="memory-import-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="memory-import-header">
          <h2 id="memory-import-title">Import Memory</h2>
          <CloseButton onClick={handleClose} />
        </div>

        <div className="memory-import-body">
          {error && <div className="memory-import-error">{error}</div>}

          {step === "paste" && (
            <>
              <p className="memory-import-lead">
                Paste this prompt into the assistant you use now, then paste its reply below.
                You'll review everything before it's saved.
              </p>

              <div className="memory-import-prompt">
                <pre>{MEMORY_EXPORT_PROMPT}</pre>
                <button type="button" className="memory-import-copy" onClick={copyPrompt}>
                  {copied ? <Tick02Icon size={14} /> : <Copy01Icon size={14} />}
                  {copied ? "Copied" : "Copy Prompt"}
                </button>
              </div>

              <textarea
                className="memory-import-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste the reply here…"
                maxLength={MEMORY_IMPORT_MAX_CHARS}
                disabled={isWorking}
                aria-label="Reply to import"
              />
              <div className="memory-import-count">
                {text.length.toLocaleString()} / {MEMORY_IMPORT_MAX_CHARS.toLocaleString()}
              </div>
              <div className="memory-import-actions">
                {(text || rows.length > 0) && (
                  <button
                    type="button"
                    className="memory-import-secondary"
                    onClick={clearDraft}
                    disabled={isWorking}
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  className="memory-import-primary"
                  onClick={runPreview}
                  disabled={!text.trim() || isWorking}
                >
                  {isWorking ? "Reading…" : "Review Memories"}
                </button>
              </div>
            </>
          )}

          {step === "review" && (
            <>
              {rows.length === 0 ? (
                <p className="memory-import-lead">
                  Buddy didn't find anything new about you in that reply. Go back and paste the full reply.
                </p>
              ) : (
                <>
                  <div className="memory-import-review-bar">
                    <span>
                      {selectedRows.length} of {rows.length} Selected
                    </span>
                    <div>
                      <button type="button" onClick={() => setAllSelected(true)}>Select All</button>
                      <button type="button" onClick={() => setAllSelected(false)}>Deselect All</button>
                    </div>
                  </div>

                  <ul className="memory-import-rows">
                    {rows.map((row) => (
                      <li key={row.key} className={`memory-import-row ${row.selected ? "" : "unselected"}`}>
                        <input
                          type="checkbox"
                          checked={row.selected}
                          onChange={(e) => updateRow(row.key, { selected: e.target.checked })}
                          aria-label="Import this memory"
                        />
                        <div className="memory-import-row-main">
                          <textarea
                            value={row.content}
                            rows={1}
                            maxLength={300}
                            onChange={(e) => updateRow(row.key, { content: e.target.value })}
                            aria-label="Memory text"
                          />
                          <div className="memory-import-badges">
                            <span className="memory-badge">{MEMORY_CATEGORY_LABELS[row.category]}</span>
                            {row.savedAt && (
                              <span className="memory-import-date">{formatSavedAt(row.savedAt)}</span>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <div className="memory-import-actions">
                <button
                  type="button"
                  className="memory-import-secondary"
                  onClick={() => setStep("paste")}
                  disabled={isWorking}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="memory-import-primary"
                  onClick={runCommit}
                  disabled={selectedRows.length === 0 || isWorking}
                >
                  {isWorking
                    ? "Importing…"
                    : selectedRows.length === 1
                      ? "Import 1 Memory"
                      : `Import ${selectedRows.length} Memories`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
